<template>
  <div
    class="historical-plugin-root"
    :class="{
      'map-fullscreen': isMapFullscreen,
      'historical-plugin-root--embedded': embeddedPreview,
    }"
  >
    <LeftJoinPlugin
      v-show="!isMapFullscreen"
      :vehicle-data-autocomplete="!embeddedPreview"
      :info-show="infoShow"
      :bill-info="billInfo"
      :type="3"
      ref="leftJoin"
      :time-range-presets="!embeddedPreview"
      :restrict-date-time-to-past="!embeddedPreview"
      :clamp-end-time-to-now="!embeddedPreview"
      :hide-search-filters="isHcPluginMode"
      :is-filter-disabled="isFilterDisabled || embeddedPreview || isLeftPanelReadonlyMode"
      :hide-search-button="embeddedPreview || isLeftPanelReadonlyMode"
      :hide-origin-destination-filters="isLeftPanelReadonlyMode"
      :trajectory-display-override="trajectoryDisplayOverride"
      :show-garage-panel="showLeftGaragePanel && !isLeftPanelReadonlyMode"
      :garage-tree-list-use-request="showLeftGaragePanel"
      :garage-tree-children-use-request="showLeftGaragePanel"
      :garage-web-crud-use-request="showLeftGaragePanel"
      @toggle="handleToggle"
      @search="handleSearch"
      @plate-color-account-change="clearHistoryAccount"
    />
    <div class="fullscreen-tip" v-if="isMapFullscreen">
      若要退出全屏模式，请按 <span class="esc-key">Esc</span>
    </div>
    <div class="historical-plugin-main">
      <div class="right-panel" v-show="!isMapFullscreen">
        <TransportServiceMapToolbar
          :tools-menu-open="toolsMenuOpen"
          :show-satellite="showSatellite"
          :hide-parking-markers="hideParkingMarkers"
          :hide-endpoint-markers="hideEndpointMarkers"
          :report-points-only="reportPointsOnly"
          @action="onTransportMapToolbarAction"
        />
      </div>
      <TrajectoryPlugIn
        :colorChange="color"
        ref="trackTrajectory"
        :track-data="migratedTrackDataForMap"
        :use-default-data="false"
        :quality-mode="true"
        :quality-restore-time="restoreTime"
        :park-mins-threshold="0"
        :force-solid-track-color="true"
        @quality-gap="onQualityGap"
        :display-primary-id="isHcPluginMode ? pluginConsigNo : tripPlateFromQuery"
        :search-area-code-override="searchAreaCodeOverride"
        :endpoint-display-override="trajectoryDisplayOverride"
        @nav-progress="handleTrackNavProgress"
        @trip-endpoint-address-lines="onTripEndpointAddressLines"
      />
      <RightDataPlugin
        v-show="!isMapFullscreen"
        ref="rightData"
        :quality-mode="true"
        :independent-export-handler="openExport"
        :is-collapsed="isCollapsed"
        @quality-settings-saved="overspeedLimit = $event.overspeedLimit"
        :is-expanded-num="isExpandedNum"
        :hide-playback-for-capture="captureHidePlayback"
        :path-navigator-active="pathNavigatorReady"
        :run-route="(routeData && routeData.runRoute) || []"
        :parking-data="parkingData"
        :trip-summary="tripSummary"
        :trip-header-override="isHcPluginMode ? pluginConsigNo : ''"
        :plate-from-query="tripPlateFromQuery"
        :display-override="trajectoryDisplayOverride"
        :map-trip-endpoint-lines="mapTripEndpointLines"
        @collapse="handleCollapse"
        @add-marker="handleAddMarker"
        @center-map="handleCenterMap"
        @locate-on-map="handleLocateOnMap"
        @path-nav-control="onPathNavControl"
        @playback-range-change="handlePlaybackRangeChange"
        @playback-progress="handlePlaybackProgress"
        @update-display-override="handleTrajectoryDisplayOverride"
        @prepare-capture-layout="prepareCaptureLayout"
        @restore-capture-ui="restoreCaptureUi"
        @apply-export-endpoint="applyExportEndpoint"
      />
    </div>
    <NativeTrackExport ref="snapshotExport" :items="exportItems" :options="exportOptions" />
  </div>
</template>

<script>
import LeftJoinPlugin from "@/components/plugin/LeftJoinPlugin.vue";
import RightDataPlugin from '@/components/plugin/RightDataPlugin.vue';
import NativeTrackExport from '@/components/business/NativeTrackExport.vue';
import { hasPermission } from '@/utils/auth';
import { createVehicleMonitoringSession, validateTrackTimeRange } from '@/views/vehicle-monitor/vehicleMonitoringSession';
import { queryIdentities, normalizeHistoryResult, qualityRunRoute } from '@/views/vehicle-monitor/nativeVehicleMonitoring';
import { queryNativeHistory } from '@/views/vehicle-monitor/nativeVehicleMonitoringApi';
import { buildTrackQuality } from '@/utils/trackQuality';
import { getSpeedCurveSettings } from '@/utils/speedCurveSettings';
import coordtransform from '@/WGS84';
import TrajectoryPlugIn from "@/views/track/trajectoryPlugIn.vue";
import TransportServiceMapToolbar from "@/views/transportService/TransportServiceMapToolbar.vue";
import { convertRunRouteToTrackData } from "./trajectoryData-track";
import { buildSearchEndpointOverride } from "@/utils/searchEndpointWarning";
import { refreshTrackTrajectoryRef } from "@/utils/amapMapKeepAlive";
import { getTrajectoryColorSettings } from "@/utils/trajectoryColorSettings";
function readInitialTrajectoryColor() {
  return getTrajectoryColorSettings().solidColor;
}

export default {
  name: "HistoricalPlugIn",
  components: {
    LeftJoinPlugin,
    RightDataPlugin,
    TrajectoryPlugIn,
    TransportServiceMapToolbar, NativeTrackExport,
  },
  props: {
    embeddedAnalysisOptions: { type: Object, default: () => ({}) },
    /** 多车轨迹等弹窗嵌入：禁用筛选、不发起查询接口 */
    embeddedPreview: {
      type: Boolean,
      default: false,
    },
    embeddedSearchForm: {
      type: Object,
      default: null,
    },
    embeddedHistoryRes: {
      type: Object,
      default: null,
    },
    /** 多车轨迹弹窗：queryTruckHisTrackNew 原始 data，供分享 localPosition */
    
  },
  data() {
    const _urlPluginType = null;
    return {
      historySession: null, nativeExportPointKeys: null, nativeIdentity: {}, nativeSearchForm: {}, nativeResult: null, gapMinutes: 15, parkingMinMinutes: 10, overspeedLimit: getSpeedCurveSettings().overspeedLimit, restoreTime: null, gapMessage: '',
      isCollapsed: _urlPluginType === "6",
      isExpandedNum: 3,
      /** 截图/导出时隐藏底部播放条 */
      captureHidePlayback: false,
      routeData: null,
      /** 与 track/trajectory(.vue) 页面同结构：{ traceList, traceOverviewInfo, parkInfo } */
      migratedTrackDataForMap: null,
      parkingData: [],
      tripSummary: {
        distance: "0km",
        duration: "0时0分0秒",
      },
      isFilterDisabled: false,
      pluginPageType: _urlPluginType,
      pluginConsigNo: "",
      tripPlateFromQuery: "",
      /** 轨迹地图子组件接管导航器，右侧走 path-nav 控制 */
      pathNavigatorReady: true,
      isMapFullscreen: false,
      showSatellite: false,
      isRanging: false,
      color: readInitialTrajectoryColor(),
      rawPluginParams: null,
      searchAreaCodeOverride: null,
      trajectoryDisplayOverride: null,
      /** 与地图起终点文字条第二行同源；null 表示尚未由地图同步 */
      mapTripEndpointLines: null,
      toolsMenuOpen: false,
      reportPointsOnly: false,
      hideEndpointMarkers: false,
      hideParkingMarkers: false,
      infoShow: false,
      billInfo: null,
      isTrajectoryShareButtonVisible:false,
      /** 货车轨迹页 queryTruckHisTrack 原始响应，供分享 */
      historyShareRawResponse: null,
      hcShareLocalPosition: null
    };
  },
  computed: {
    nativeQuality() { return buildTrackQuality(this.nativeResult ? this.nativeResult.points : [], { gapMinutes: this.gapMinutes, parkingMinMinutes: this.parkingMinMinutes, overspeedLimit: this.overspeedLimit }) },
    exportItems() {
      const items = this.embeddedPreview && this.nativeResult ? [{ ...this.nativeIdentity, key: this.nativeIdentity.vehicleNo + '_' + this.nativeIdentity.plateColor, status: this.nativeResult.resultStatus, result: this.nativeResult, message: '' }] : this.historySession ? this.historySession.items : [];
      if (!this.nativeExportPointKeys) return items;
      const keys = new Set(this.nativeExportPointKeys);
      return items.map(item => item.result ? { ...item, result: { ...item.result, points: (item.result.points || []).filter(point => keys.has(point.pointKey)) } } : item);
    },
    exportOptions() { return { startTime: Date.parse(String(this.nativeSearchForm.qryBtm).replace(' ', 'T') + '+08:00'), endTime: Date.parse(String(this.nativeSearchForm.qryEtm).replace(' ', 'T') + '+08:00'), gapMinutes: this.gapMinutes, parkingMinMinutes: this.parkingMinMinutes, overspeedLimit: this.overspeedLimit } },
    /** 危化运单 / 分享轨迹：不展示左侧筛选与车库，不请求车库相关接口 */
    isHcPluginMode() { return false },
    isHistoryPageMode() { return true },
    isHistoryShareView() { return false },
    isLeftPanelReadonlyMode() { return false },
    
    
    
    showLeftGaragePanel() {
      return !this.embeddedPreview && !this.isHcPluginMode
    },
  },
  watch: {
    overspeedLimit() { this.rebuildQuality() },
    parkingMinMinutes() { this.rebuildQuality() },
    migratedTrackDataForMap() {
      this._resetPluginMapToolbarForNewTrajectory();
    },
    embeddedHistoryRes: {
      deep: true,
      handler() {
        if (this.embeddedPreview) {
          this.$nextTick(() => {
            this.bootstrapEmbeddedPreview();
          });
        }
      },
    },
    
  },
  activated() {
    this.resizePluginMapLayout();
  },
  deactivated() {
    this.historySession.cancel();
    this.pauseRightDataPlaybackOnTabHide();
  },
  created() { this.historySession = createVehicleMonitoringSession(item => queryNativeHistory(item, this.nativeSearchForm)) },
  mounted() {
 this.color = readInitialTrajectoryColor(); window.addEventListener('keydown', this.handleKeydown);
 if (this.embeddedPreview) { this.isFilterDisabled = true; this.$nextTick(() => this.bootstrapEmbeddedPreview()); return }
 const query = this.$route.query || {};
 if (query.vclN && query.vco) this.$nextTick(() => this.$refs.leftJoin.updateSearchForm({ vclN: query.vclN, vco: query.vco }));
},
  beforeDestroy() { this.historySession.cancel(); this.pauseRightDataPlaybackOnTabHide(); window.removeEventListener('keydown', this.handleKeydown) },
  methods: {
    clearHistoryAccount() { if (this.$refs.snapshotExport) this.$refs.snapshotExport.canceled = true; this.nativeExportPointKeys = null; this.pauseRightDataPlaybackOnTabHide(); this.historySession.cancel(); this.historySession.items = []; this.nativeIdentity = {}; this.nativeSearchForm = {}; this.nativeResult = null; this.trajectoryDisplayOverride = null; this.tripPlateFromQuery = ''; this.searchAreaCodeOverride = null; this.restoreTime = null; this.gapMessage = ''; this.rebuildQuality() },
    can: hasPermission,
    formatNativeTime(value) { return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(new Date(value)) },
    statusLabel(status) { return { PENDING: '待查询', LOADING: '查询中', SUCCESS: '成功', NO_DATA: '无数据', FAILED: '失败', CANCELED: '已取消' }[status] || status },
    applyHistorySession() {
      const item = this.historySession.items[0];
      if (item && item.status === 'SUCCESS') return this.applyTruckHistoryResponse(item.result);
      this.nativeResult = null;
      this.rebuildQuality();
      if (item && item.status === 'FAILED') this.$message.error(item.message || '轨迹查询失败');
      if (item && item.status === 'NO_DATA') this.$message.warning(item.message || '暂无轨迹数据');
    },
    async retryHistory(key) { await this.historySession.retry(key); this.applyHistorySession() },
    async openExport(options = {}) {
      if (!hasPermission('truck:track') || !hasPermission('truck:export')) return this.$message.warning('没有轨迹导出权限');
      this.nativeExportPointKeys = Array.isArray(options.pointKeys) ? options.pointKeys : null;
      await this.$nextTick();
      const exporter = this.$refs.snapshotExport;
      if (!exporter) return;
      exporter.addressMode = ['NONE', 'PARKING', 'ALL'].includes(options.addressMode) ? options.addressMode : 'NONE';
      exporter.open();
      if (options.addressMode) {
        try { await exporter.download(); } finally { this.nativeExportPointKeys = null; }
      }
    },
    onQualityGap(payload) { this.gapMessage = payload.skippedSeconds ? '已跳过缺报 ' + payload.skippedSeconds + ' 秒，定位到下个真实点' : '缺报空档内没有定位，不插值' },
    changeParking(value, oldValue) { if (!Number.isInteger(value) || value < 0) { this.parkingMinMinutes = oldValue; this.$message.warning('本地停车分析阈值须为非负整数分钟') } },
    changeGap(value, oldValue) { if (!Number.isInteger(value) || value < 1 || value > 120) { this.gapMinutes = oldValue; return this.$message.warning('缺报阈值须为1—120整数分钟') } const right = this.$refs.rightData; this.restoreTime = right && this.nativeQuality.points.length ? this.nativeQuality.points[0].locationTime + right.playElapsedSeconds * 1000 : null; this.pauseRightDataPlaybackOnTabHide(); this.rebuildQuality() },
    rebuildQuality() {
      const quality = this.nativeQuality; const runRoute = qualityRunRoute(quality.points, this.formatNativeTime).map(point => ({ ...point, vclN: this.tripPlateFromQuery }));
      this.parkingData = quality.parkings.map(point => ({ ...point, lon: point.longitude * 600000, lat: point.latitude * 600000, duration: point.durationSeconds + '秒', startTime: this.formatNativeTime(point.startTime), endTime: this.formatNativeTime(point.endTime), pointIndex: quality.points.findIndex(item => item.locationTime === point.endTime), location: point.address }));
      this.routeData = { ...(this.nativeResult || {}), runRoute, parkingData: this.parkingData, mileage: quality.distance };
      const map = convertRunRouteToTrackData(runRoute, { totalMileage: quality.distance, parkArray: quality.parkings.map(point => ({ parkBte: point.startTime, parkEte: point.endTime, parkLon: point.longitude * 600000, parkLat: point.latitude * 600000, parkAdr: point.address })) });
      map.segmentKeys = quality.points.map(point => point.segmentKey); map.qualityPoints = quality.points; this.migratedTrackDataForMap = map;
      this.tripSummary = { distance: quality.distance == null ? '不可计算' : quality.distance.toFixed(2) + 'km', duration: Math.floor(quality.totalSeconds / 3600) + '时' + Math.floor(quality.totalSeconds % 3600 / 60) + '分' + quality.totalSeconds % 60 + '秒' };
      if (this.$refs.leftJoin) this.$refs.leftJoin.updateRouteData(this.routeData);
    },
    handleTrajectoryDisplayOverride(payload) {
      const base = payload && typeof payload === 'object' ? payload : {}
      this.trajectoryDisplayOverride = Object.keys(base).length ? { ...base } : null
    },
    resizePluginMapLayout() {
      this.$nextTick(() => {
        refreshTrackTrajectoryRef(this);
      });
    },
    /** 切页时强制右侧播放态为暂停，避免 keep-alive 下按钮仍显示播放中 */
    pauseRightDataPlaybackOnTabHide() {
      const rightData = this.$refs.rightData;
      if (!rightData || !rightData.isPlaying) return;
      if (typeof rightData.pauseTrack === 'function') {
        rightData.pauseTrack();
        return;
      }
      rightData.isPlaying = false;
      if (typeof rightData.stopPlaybackTimer === 'function') {
        rightData.stopPlaybackTimer();
      }
      rightData.playbackCompletedInRange = false;
    },
    applyTrajectoryColorConfig(config) {
      if (!config) return;
      this.color = config.solidColor;
    },
    toggleTools() {
      this.toolsMenuOpen = !this.toolsMenuOpen;
    },
    onTransportMapToolbarAction(payload) {
      if (!payload || !payload.type) return;
      switch (payload.type) {
        case "toggleToolsMenu":
          this.toggleTools();
          break;
        case "toggleHideParking":
          this.toggleHideParkingPoints();
          break;
        case "toggleHideEndpoints":
          this.toggleHideEndpointMarkers();
          break;
        case "toggleReportPoints":
          this.toggleReportLocation();
          break;
        case "ranging":
          this.toggleRanging();
          break;
        case "zoomIn":
          this.zoomIn();
          break;
        case "zoomOut":
          this.zoomOut();
          break;
        case "toggleSatellite":
          this.toggleSatelliteLayer();
          break;
        case "toggleFullscreen":
          this.toggleMapFullscreen();
          break;
        case "colorConfig":
          if (payload.value) this.applyTrajectoryColorConfig(payload.value);
          break;
        default:
          break;
      }
    },
    _toggleTrackTool(stateKey, mapMethod) {
      const m = this.getTrackMapRef();
      if (!m || typeof m[mapMethod] !== "function") return;
      const next = !this[stateKey];
      if (!m[mapMethod](next)) return;
      this[stateKey] = next;
    },
    toggleReportLocation() {
      this._toggleTrackTool("reportPointsOnly", "setPathLineOnlyHidden");
    },
    toggleHideEndpointMarkers() {
      this._toggleTrackTool("hideEndpointMarkers", "setEndpointMarkersHidden");
    },
    toggleHideParkingPoints() {
      this._toggleTrackTool("hideParkingMarkers", "setParkingMarkersHidden");
    },
    _syncPluginMapToolbarFromTrack(track) {
      if (!track) return;
      this.reportPointsOnly = !!track.hidePathLineOnly;
      this.hideEndpointMarkers = !!track.endpointMarkersHidden;
      this.hideParkingMarkers = !!track.parkingMarkersHidden;
    },
    _resetPluginMapToolbarForNewTrajectory() {
      this.toolsMenuOpen = false;
      this.reportPointsOnly = false;
      this.hideEndpointMarkers = false;
      this.hideParkingMarkers = false;
      this.$nextTick(() => {
        const m = this.$refs.trackTrajectory;
        if (!m) return;
        if (typeof m.setPathLineOnlyHidden === "function")
          m.setPathLineOnlyHidden(false);
        if (typeof m.setEndpointMarkersHidden === "function")
          m.setEndpointMarkersHidden(false);
        if (typeof m.setParkingMarkersHidden === "function")
          m.setParkingMarkersHidden(false);
      });
    },
    parseLngLatText(text) {
      if (text == null || String(text).trim() === "") return null;
      const parts = String(text)
        .trim()
        .split(/[,，\s]+/)
        .map((x) => Number(x));
      if (
        parts.length < 2 ||
        !Number.isFinite(parts[0]) ||
        !Number.isFinite(parts[1])
      )
        return null;
      return [parts[0], parts[1]];
    },
    formatGtmToText(gtm) {
      if (!gtm || String(gtm).length < 15) return "";
      const d = String(gtm).substring(0, 8);
      const t = String(gtm).substring(9);
      if (!d || !t || t.length < 6) return "";
      return `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(
        6,
        8
      )} ${t.substring(0, 2)}:${t.substring(2, 4)}:${t.substring(4, 6)}`;
    },
    
    getTrackMapRef() {
      const comp = this.$refs.trackTrajectory;
      if (!comp) {
        this.$message.warning("地图尚未初始化");
        return null;
      }
      return comp;
    },
    async toggleRanging() {
      const trackMap = this.getTrackMapRef();
      if (!trackMap || typeof trackMap.toggleRanging !== "function") return;
      try {
        this.isRanging = await trackMap.toggleRanging();
      } catch (e) {
        this.$message.error("测距功能加载失败");
      }
    },
    toggleSatelliteLayer() {
      const trackMap = this.getTrackMapRef();
      if (!trackMap || typeof trackMap.toggleSatelliteLayer !== "function")
        return;
      this.showSatellite = trackMap.toggleSatelliteLayer();
    },
    zoomIn() {
      const trackMap = this.getTrackMapRef();
      if (!trackMap || typeof trackMap.zoomInMap !== "function") return;
      trackMap.zoomInMap();
    },
    zoomOut() {
      const trackMap = this.getTrackMapRef();
      if (!trackMap || typeof trackMap.zoomOutMap !== "function") return;
      trackMap.zoomOutMap();
    },
    toggleMapFullscreen() {
      this.isMapFullscreen = !this.isMapFullscreen;
    },
    handleKeydown(e) {
      if (e.key === "Escape" && this.isMapFullscreen) {
        this.isMapFullscreen = false;
      }
    },
    handleCollapse(num) {
      this.isExpandedNum = num;
    },
    handleToggle(collapsed) {
      this.isCollapsed = collapsed;
      this.$nextTick(() => {
        setTimeout(() => {
          const track = this.$refs.trackTrajectory;
          if (!track) return;
          if (typeof track.scheduleFitMapToTrack === "function") {
            track.scheduleFitMapToTrack();
          } else if (typeof track.fitMapToAllTrackOverlays === "function") {
            track.fitMapToAllTrackOverlays();
          }
          if (typeof track.scheduleEnsureEndpointLabelsInView === "function") {
            track.scheduleEnsureEndpointLabelsInView(280);
          }
        }, 320);
      });
    },
    prepareCaptureLayout(layout = {}) {
      const track = this.$refs.trackTrajectory;
      const done =
        layout && typeof layout.done === "function" ? layout.done : null;
      const viewSnap =
        track && typeof track.snapshotMapView === "function"
          ? track.snapshotMapView()
          : null;
      if (track && typeof track.lockMapViewFit === "function") {
        track.lockMapViewFit();
      }

      this.isCollapsed =
        layout.isCollapsed === undefined ? true : Boolean(layout.isCollapsed);
      this.isExpandedNum = Number.isFinite(Number(layout.isExpandedNum))
        ? Number(layout.isExpandedNum)
        : 3;
      // 截图/详情导出默认隐藏底部播放条；显式传 false 时恢复
      this.captureHidePlayback = layout.hidePlayback === false ? false : true;
      const leftJoin = this.$refs.leftJoin;
      if (leftJoin && typeof leftJoin.forceCollapseForCapture === "function") {
        leftJoin.forceCollapseForCapture();
      } else if (leftJoin) {
        leftJoin.isCollapsed = true;
        if (typeof leftJoin.$emit === "function") {
          leftJoin.$emit("toggle", true);
        }
      }

      this.$nextTick(() => {
        setTimeout(async () => {
          try {
            const map =
              (track && track.mapInstance) ||
              (typeof window !== "undefined" ? window.__AMAP_MAP__ : null) ||
              null;
            if (map && typeof map.resize === "function") {
              try {
                map.resize();
              } catch (e) {
                /* ignore */
              }
            }
            if (track && typeof track.unlockMapViewFit === "function") {
              track.unlockMapViewFit();
            }
            // 隐藏播放条后地图变高，必须重新 fit，否则南端仍贴在旧底边被裁切
            if (track && typeof track.prepareMapForCapture === "function") {
              await track.prepareMapForCapture({ preserveView: false });
            } else if (
              track &&
              typeof track.fitMapToAllTrackOverlays === "function"
            ) {
              track.fitMapToAllTrackOverlays({
                capture: true,
                immediately: true,
              });
            } else if (
              track &&
              typeof track.restoreMapView === "function" &&
              viewSnap
            ) {
              track.restoreMapView(viewSnap);
            }
          } catch (e) {
            /* ignore */
          } finally {
            if (done) done();
          }
        }, 360);
      });
    },
    restoreCaptureUi() {
      this.captureHidePlayback = false;
    },
    applyExportEndpoint(payload = {}) {
      // 截图与数据导出各自独立：每次确认整表替换，空字段清除另一侧残留
      const clean = (value) => (value == null ? '' : String(value).trim())
      const startLonlat = clean(payload.startLonlat) || clean(payload.loadLonlat)
      const endLonlat = clean(payload.endLonlat) || clean(payload.unloadLonlat)
      const startAddress =
        clean(payload.startAddress) ||
        clean(payload.loadingAddress) ||
        clean(payload.loadAddress)
      const endAddress =
        clean(payload.endAddress) ||
        clean(payload.unloadingAddress) ||
        clean(payload.unloadAddress)
      this.searchAreaCodeOverride = buildSearchEndpointOverride({
        loadLonlat: clean(payload.loadLonlat),
        loadAddress: clean(payload.loadAddress),
        unloadLonlat: clean(payload.unloadLonlat),
        unloadAddress: clean(payload.unloadAddress),
        startLonlat,
        endLonlat,
        startAddress,
        endAddress,
        startAreaCode: clean(payload.startAreaCode),
        endAreaCode: clean(payload.endAreaCode)
      })
      this.trajectoryDisplayOverride = {
        start: {
          businessLabel: startAddress ? '装货地' : '',
          businessAddress: startAddress
        },
        end: {
          businessLabel: endAddress ? '卸货地' : '',
          businessAddress: endAddress
        }
      }
    },
    handleAddMarker() {},
    handleCenterMap() {},
    handleLocateOnMap(data) {
      const track = this.$refs.trackTrajectory;
      if (track && typeof track.locateOnMap === "function") {
        track.locateOnMap(data);
      }
    },
    onPathNavControl(payload) {
      const track = this.$refs.trackTrajectory;
      if (track && typeof track.onPathNavControl === "function") {
        track.onPathNavControl(payload);
      }
    },
    handlePlaybackRangeChange(payload) {
      const track = this.$refs.trackTrajectory;
      if (track && typeof track.applyPlaybackRangeClip === "function") {
        track.applyPlaybackRangeClip(payload);
      }
    },
    handlePlaybackProgress() {},
    handleTrackNavProgress(payload) { const right = this.$refs.rightData; if (right && payload) right.syncFromPathNavigatorRatio(payload.ratio, payload.inGap ? { lon: null, lat: null } : payload.navPos) },
    onTripEndpointAddressLines(payload) {
      if (!payload || typeof payload !== "object") {
        this.mapTripEndpointLines = null;
        return;
      }
      this.mapTripEndpointLines = {
        start: payload.start != null ? String(payload.start).trim() : "",
        end: payload.end != null ? String(payload.end).trim() : "",
      };
    },

    /** getPluginUrl 响应体：兼容 { data: TruckHisTrackResp } 与扁平结构 */
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    
    applyTruckHistoryResponse(result) { this.nativeResult = normalizeHistoryResult(result, this.nativeIdentity); this.rebuildQuality() },
    async handleSearch(searchForm) {
 if (this.embeddedPreview) return;
 try {
  if (!hasPermission('truck:track')) throw new Error('没有货车轨迹权限');
  const identity = queryIdentities({ ...searchForm, vehicleMode: 'single' })[0];
  validateTrackTimeRange(Date.parse(searchForm.qryBtm.replace(' ', 'T') + '+08:00'), Date.parse(searchForm.qryEtm.replace(' ', 'T') + '+08:00'));
  const conditions = { ...searchForm };
  for (const key of ['startLonlat', 'endLonlat']) { if (!conditions[key]) continue; const pair = String(conditions[key]).split(',').map(value => value.trim() === '' ? NaN : Number(value)); if (pair.length !== 2 || pair.some(value => !Number.isFinite(value)) || Math.abs(pair[0]) > 180 || Math.abs(pair[1]) > 90) throw new Error('请填写有效的起终点坐标'); conditions[key] = coordtransform.gcj02towgs84(pair[0], pair[1]).map(value => value.toFixed(6)).join(',') }
  this.pauseRightDataPlaybackOnTabHide(); this.nativeIdentity = identity; this.nativeSearchForm = conditions; this.tripPlateFromQuery = identity.vehicleNo;
  this.searchAreaCodeOverride = buildSearchEndpointOverride(searchForm); this.trajectoryDisplayOverride = null; this.nativeResult = null; this.restoreTime = null; this.gapMessage = ''; this.rebuildQuality();
  if (conditions.parkMins != null && conditions.parkMins !== '') { const park = Number(conditions.parkMins); if (!Number.isFinite(park) || park < 0) throw new Error('上游停车筛选须为非负数') }
  await this.historySession.start([identity]); this.applyHistorySession();
 } catch (error) { this.$message.error(error.msg || error.message || '轨迹查询失败') }
},
    
    
    
    async bootstrapEmbeddedPreview() { if (!this.embeddedPreview) return; this.isFilterDisabled = true; this.isCollapsed = false; const analysis = this.embeddedAnalysisOptions; if (Number.isInteger(analysis.gapMinutes) && analysis.gapMinutes >= 1 && analysis.gapMinutes <= 120) this.gapMinutes = analysis.gapMinutes; if (Number.isInteger(analysis.parkingMinMinutes) && analysis.parkingMinMinutes >= 0) this.parkingMinMinutes = analysis.parkingMinMinutes; if (Number.isFinite(analysis.overspeedLimit)) this.overspeedLimit = analysis.overspeedLimit; const form = this.embeddedSearchForm || {}; this.nativeIdentity = { vehicleNo: form.vclN || form.vehicleNo || '', plateColor: String(form.vco || form.plateColor || '') }; this.nativeSearchForm = { ...form }; this.tripPlateFromQuery = this.nativeIdentity.vehicleNo; await this.$nextTick(); if (this.$refs.leftJoin) this.$refs.leftJoin.updateSearchForm(form); if (this.embeddedHistoryRes) this.applyTruckHistoryResponse(this.embeddedHistoryRes); this.resizePluginMapLayout() },
  },
};
</script>
<style scoped>
.historical-plugin-root {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.historical-plugin-root--embedded {
  width: 100%;
  height: 100%;
}

.historical-plugin-root--embedded.map-fullscreen {
  width: 100%;
  height: 100%;
}


.historical-plugin-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
}

/* 与 index.vue 一致的地图右上角工具条 */
.right-panel {
  flex: 3;
  border-radius: 0.25rem;
  padding: 0.9375rem;
  position: absolute;
  z-index: 9;
  //width: 11.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  right: 0;
  top: 0;
  pointer-events: none;
}

.fullscreen-tip {
  position: absolute;
  left: 50%;
  top: 1rem;
  transform: translateX(-50%);
  z-index: 10001;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 0.8125rem;
  line-height: 1.3;
}

.esc-key {
  display: inline-block;
  margin-left: 0.25rem;
  padding: 0 0.3125rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.16);
  border: 0.0625rem solid rgba(255, 255, 255, 0.35);
  font-weight: 600;
}

/* 全屏时地图铺满视口 */
.map-fullscreen .historical-plugin-main {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}
</style>
